import React from 'react';
import { QuestionnaireItem } from '../QuestionnairePreview';
// @ts-ignore
import styles from './../QuestionnairePreview.module.scss';
import { QuestionnaireItemSubtype } from '../../../Enums/QuestionnaireItemSubtype.enum';
import QuestionnairePreviewItems from '../QuestionnairePreviewItems';
import {
	getColorsByValue,
	rangeGenerator,
} from '../../../Services/questionnaire.service';
import TextArea from '../../../../../Components/Base/TextArea/TextArea';
import Checkbox from '../../../../../Components/Base/Checkbox/Checkbox';
import useTranslation from '../../../../../Hooks/useTranslation';
import Text from '../../../../../Components/Base/Text/Text';
import Input from '../../../../../Components/Form/components/Input';

interface Props {
	item: QuestionnaireItem;
}

const QuestionnairePreviewAnswer: React.FC<Props> = ({ item }) => {
	const { t } = useTranslation();

	const renderItem = ({
		title,
		note,
		content,
	}: {
		title: string;
		note?: string;
		content: React.ReactElement;
	}) => {
		return (
			<>
				<div className={styles.possibleAnswers}>
					{t(title)}:{note && <div className={styles.note}>{t(note)}</div>}
				</div>
				{content}
			</>
		);
	};

	let component;

	switch (item.subtype) {
		case QuestionnaireItemSubtype.RADIO_LIST:
			component = renderItem({
				title: 'component.questionnairePreview.possibleAnswers',
				note: 'component.questionnairePreview.hintRadioList',
				content: (
					<div className={styles.radioList}>
						{item.children?.map(subItem => {
							const colors = getColorsByValue(
								subItem.value,
								item?.settings?.resultColoring,
							);
							return (
								<div key={subItem.id}>
									<div className={styles.radioItem}>
										<div className={styles.radio} />
										<div className={styles.title}>{subItem.title}</div>
										{colors && (
											<div
												className={styles.colorSphere}
												style={{
													backgroundColor: colors?.backgroundColor,
												}}
											/>
										)}
									</div>
									<QuestionnairePreviewItems children={subItem.children} />
								</div>
							);
						})}
					</div>
				),
			});
			break;
		case QuestionnaireItemSubtype.CHECKBOX_LIST:
			component = renderItem({
				title: 'component.questionnairePreview.possibleAnswers',
				note: 'component.questionnairePreview.hintCheckbox',
				content: (
					<div className={styles.radioList}>
						{item.children?.map(subItem => (
							<React.Fragment key={subItem.id}>
								<Checkbox tx={subItem.title} />
								<QuestionnairePreviewItems children={subItem.children} />
							</React.Fragment>
						))}
					</div>
				),
			});
			break;
		case QuestionnaireItemSubtype.SCALE_LIST:
			component = renderItem({
				title: 'component.questionnairePreview.possibleAnswers',
				content: (
					<div style={{ display: 'flex', gap: '5px' }}>
						{rangeGenerator(1, item.value, 1).map((i, index) => {
							const colors = getColorsByValue(
								i,
								item?.settings?.resultColoring,
							);
							return (
								<div
									key={index}
									className={styles.scaleListItem}
									style={{
										backgroundColor: colors?.backgroundColor ?? '#929292',
										color: colors?.textColor ?? '#fff',
									}}>
									{i}
								</div>
							);
						})}
					</div>
				),
			});
			break;
		case QuestionnaireItemSubtype.TEXTAREA:
			component = (
				<div style={{ maxWidth: '800px' }}>
					{item.title && <div className={styles.label}>{item.title}</div>}
					<TextArea placeholder={item?.settings?.placeholder} disabled />
				</div>
			);
			break;
		case QuestionnaireItemSubtype.DATE:
		case QuestionnaireItemSubtype.NUMBER_FIELD:
			component = (
				<div style={{ maxWidth: '800px' }}>
					{item.title && <div className={styles.label}>{item.title}</div>}
					<Input placeholder={item?.settings?.placeholder} disabled />
				</div>
			);
			break;
		default:
			component = <Text tx='component.questionnairePreview.unsupportedItem' />;
	}

	return (
		<>
			{item.description && (
				<div className={styles.description}>
					{t('component.questionnairePreview.manualForAnswer')}:{' '}
					{item.description}
				</div>
			)}
			{component}
		</>
	);
};

export default QuestionnairePreviewAnswer;
